import {
  Phone, MessageSquare, Mail, FileText, Users, Contact2, Filter, Briefcase,
  Calendar, ListChecks, ClipboardList, Workflow, DollarSign, CreditCard, Building2, BarChart3,
  Cloud, FolderOpen, Layers, Heart, Headphones, Star, MessageSquareText,
  Bell, Share2, Zap, GitBranch, Bot, Sparkles, Link2, SkipForward, ArrowLeftRight, QrCode, ShieldCheck,
} from "lucide-react";

const EXISTING = (providers, label = "Connect Existing") => ({ key: "existing", kind: "provider_picker", label, icon: Link2, providers });
const SKIP = { key: "skip", kind: "skip", label: "Skip for Now", icon: SkipForward };
const GENERIC_NOVA = (label, fields) => ({ key: "nova", kind: "nova_wizard", label, icon: Sparkles, fields });

export const CONNECTIONS_CATALOG = [
  {
    key: "customer_intake",
    number: 1,
    label: "Customer Intake",
    subtitle: "QR codes, forms and identity verification",
    icon: QrCode,
    iconBg: "bg-blue-100 dark:bg-blue-500/10",
    iconColor: "text-blue-600 dark:text-blue-400",
    previewIcons: [QrCode, FileText, ShieldCheck],
    subCapabilities: [
      {
        key: "web_form", label: "Forms, QR Codes & Verification",
        methods: [
          { key: "manage", kind: "navigate", label: "Manage Customer Intake", icon: Sparkles, path: "/dashboard/connections/customer-intake" },
        ],
      },
    ],
  },
  {
    key: "customer_management",
    number: 2,
    label: "Customer Management",
    subtitle: "Manage leads, customers and customer information",
    icon: Users,
    iconBg: "bg-amber-100 dark:bg-amber-500/10",
    iconColor: "text-amber-600 dark:text-amber-400",
    previewIcons: [Users, Contact2, Filter, Briefcase],
    subCapabilities: [
      {
        key: "crm", label: "CRM",
        methods: [
          EXISTING([{ key: "salesforce", label: "Salesforce" }, { key: "hubspot", label: "HubSpot" }, { key: "other", label: "Other CRM" }], "Connect Existing CRM"),
          { key: "nova_crm", kind: "nova_wizard", label: "Use Nova CRM", icon: Users, fields: [
            { key: "lifecycle_info", label: "Nova CRM Lifecycle", type: "info", text: "Potential Customer → Lead → Customer" },
            { key: "default_owner_team", label: "Default Owner Team (optional)", type: "text", placeholder: "e.g. Sales team" },
          ] },
          SKIP,
        ],
      },
    ],
  },
  {
    key: "scheduling_operations",
    number: 3,
    label: "Scheduling & Operations",
    subtitle: "Calendar, appointments, jobs, tasks and workflows",
    icon: Calendar,
    iconBg: "bg-violet-100 dark:bg-violet-500/10",
    iconColor: "text-violet-600 dark:text-violet-400",
    previewIcons: [Calendar, ListChecks, ClipboardList, Workflow],
    subCapabilities: [
      { key: "calendar", label: "Calendar", methods: [
        EXISTING([{ key: "google_calendar", label: "Google Calendar" }, { key: "microsoft_outlook", label: "Microsoft Outlook" }, { key: "other", label: "Other Calendar" }]),
        GENERIC_NOVA("Use Nova Calendar", [{ key: "default_view", label: "Default View", type: "select", options: ["Day", "Week", "Month"] }]),
        SKIP,
      ] },
      { key: "appointments", label: "Scheduling & Appointments", methods: [
        EXISTING([{ key: "other", label: "Other Scheduling Tool" }]),
        GENERIC_NOVA("Use Nova Scheduling", [
          { key: "appointment_duration", label: "Default Appointment Duration (minutes)", type: "text", placeholder: "60" },
          { key: "business_hours", label: "Business Hours", type: "text", placeholder: "e.g. Mon–Fri 8am–6pm" },
          { key: "team_assignment", label: "Team Assignment", type: "text", placeholder: "e.g. Field Techs" },
          { key: "service_association", label: "Associate Appointments With Services", type: "toggle", default: true },
        ]),
        SKIP,
      ] },
      { key: "jobs_work_orders", label: "Jobs & Work Orders", methods: [
        EXISTING([{ key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Jobs & Work Orders", [
          { key: "default_status_flow", label: "Default Job Status Flow", type: "text", placeholder: "Scheduled → In Progress → Completed" },
        ]),
        SKIP,
      ] },
      { key: "tasks", label: "Tasks", methods: [
        EXISTING([{ key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Tasks", [
          { key: "default_assignee_mode", label: "Default Assignment", type: "select", options: ["Assign to team", "Assign to individual"] },
        ]),
        SKIP,
      ] },
      { key: "team_workflows", label: "Team Workflows", methods: [
        EXISTING([{ key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Team Workflows", [
          { key: "workflow_notes", label: "Notes", type: "textarea", placeholder: "Describe how work should move between your team" },
        ]),
        SKIP,
      ] },
    ],
  },
  {
    key: "finance",
    number: 4,
    label: "Finance",
    subtitle: "Estimates, invoices, payments and accounting",
    icon: DollarSign,
    iconBg: "bg-emerald-100 dark:bg-emerald-500/10",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    previewIcons: [FileText, CreditCard, Building2, BarChart3],
    subCapabilities: [
      {
        key: "estimates_proposals", label: "Estimates & Proposals",
        methods: [
          { key: "configure", kind: "navigate", label: "Configure Estimates & Proposals", icon: Sparkles, path: "/dashboard/finance/estimates-proposals" },
        ],
      },
      {
        key: "payment_reminders", label: "Payment Reminders",
        methods: [
          { key: "configure", kind: "navigate", label: "Configure Payment Reminders", icon: Bell, path: "/dashboard/finance/payment-reminders" },
        ],
      },
      { key: "customer_invoicing", label: "Customer Invoicing", comingSoon: true, methods: [] },
      { key: "payments", label: "Payments", comingSoon: true, methods: [] },
      { key: "accounting_connections", label: "Accounting Connections", comingSoon: true, methods: [] },
    ],
  },
  {
    key: "documents_productivity",
    number: 5,
    label: "Documents & Productivity",
    subtitle: "Files, documents and productivity tools",
    icon: FileText,
    iconBg: "bg-blue-100 dark:bg-blue-500/10",
    iconColor: "text-blue-600 dark:text-blue-400",
    previewIcons: [FileText, Cloud, FolderOpen, Layers],
    subCapabilities: [
      { key: "file_storage", label: "Documents & Storage", methods: [
        EXISTING([{ key: "google_drive", label: "Google Drive" }, { key: "microsoft_onedrive", label: "Microsoft OneDrive / SharePoint" }, { key: "other", label: "Other Storage" }]),
        GENERIC_NOVA("Use Nova Documents", [
          { key: "storage_notes", label: "Notes", type: "textarea", placeholder: "What kinds of files will you store here?" },
        ]),
        SKIP,
      ] },
    ],
  },
  {
    key: "customer_experience",
    number: 6,
    label: "Customer Experience",
    subtitle: "Requests, support, feedback and reviews",
    icon: Heart,
    iconBg: "bg-rose-100 dark:bg-rose-500/10",
    iconColor: "text-rose-600 dark:text-rose-400",
    previewIcons: [MessageSquareText, Headphones, Star, Heart],
    subCapabilities: [
      { key: "customer_requests", label: "Customer Requests", methods: [
        EXISTING([{ key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Customer Requests", [
          { key: "routing_team", label: "Routing / Team Ownership", type: "text", placeholder: "e.g. Support team" },
          { key: "categories", label: "Request Categories (comma separated)", type: "text", placeholder: "Billing, Service, General" },
        ]),
        SKIP,
      ] },
      { key: "support", label: "Support", methods: [
        EXISTING([{ key: "zendesk", label: "Zendesk" }, { key: "intercom", label: "Intercom" }, { key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Support", [{ key: "notes", label: "Notes", type: "textarea" }]),
        SKIP,
      ] },
      { key: "feedback", label: "Feedback", methods: [
        EXISTING([{ key: "other", label: "Other Platform" }]),
        GENERIC_NOVA("Use Nova Feedback", [{ key: "notes", label: "Notes", type: "textarea" }]),
        SKIP,
      ] },
      { key: "reviews", label: "Reviews", methods: [
        EXISTING([{ key: "google_reviews", label: "Google Reviews" }, { key: "other", label: "Other Platform" }]),
        GENERIC_NOVA("Use Nova Reviews", [{ key: "notes", label: "Notes", type: "textarea" }]),
        SKIP,
      ] },
    ],
  },
  {
    key: "team_communication",
    number: 7,
    label: "Team Communication",
    subtitle: "Team communication, notifications and collaboration",
    icon: MessageSquare,
    iconBg: "bg-sky-100 dark:bg-sky-500/10",
    iconColor: "text-sky-600 dark:text-sky-400",
    previewIcons: [MessageSquare, Users, Bell, Share2],
    subCapabilities: [
      { key: "chat_collaboration", label: "Chat & Collaboration", methods: [
        EXISTING([{ key: "slack", label: "Slack" }, { key: "microsoft_teams", label: "Microsoft Teams" }, { key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Team Chat", [{ key: "notes", label: "Notes", type: "textarea" }]),
        SKIP,
      ] },
      { key: "notifications", label: "Notifications", methods: [
        EXISTING([{ key: "other", label: "Other Tool" }]),
        GENERIC_NOVA("Use Nova Notifications", [{ key: "notes", label: "Notes", type: "textarea" }]),
        SKIP,
      ] },
    ],
  },
  {
    key: "automation_ai",
    number: 8,
    label: "Automation & AI",
    subtitle: "Automate processes and get AI assistance",
    icon: Zap,
    iconBg: "bg-purple-100 dark:bg-purple-500/10",
    iconColor: "text-purple-600 dark:text-purple-400",
    previewIcons: [Zap, GitBranch, Bot, Sparkles],
    disabled: true,
    subCapabilities: [],
  },
];

export const STATUS_LABELS = {
  not_configured: "Not Configured",
  setup_in_progress: "Setup In Progress",
  pending_connection: "Pending Connection",
  pending_provisioning: "Pending Provisioning",
  active: "Active",
  connected: "Connected",
  skipped: "Skipped",
};

export const STATUS_STYLES = {
  not_configured: "border-slate-200 text-slate-500 dark:border-white/15 dark:text-slate-400",
  setup_in_progress: "border-blue-200 text-blue-600 dark:border-blue-500/30 dark:text-blue-400",
  pending_connection: "border-amber-200 text-amber-600 dark:border-amber-500/30 dark:text-amber-400",
  pending_provisioning: "border-orange-200 text-orange-600 dark:border-orange-500/30 dark:text-orange-400",
  active: "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400",
  connected: "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400",
  skipped: "border-slate-200 text-slate-400 dark:border-white/10 dark:text-slate-500",
};

export const CONFIGURED_STATUSES = new Set(["setup_in_progress", "pending_connection", "pending_provisioning", "active", "connected", "skipped"]);
